import { Alert } from '@neo/primitives/alert'
import { ButtonWithLoader } from '@neo/primitives/button'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { PRIVATE_FIELD_CLASS } from 'v2/configs/constants'
import { billingService } from 'v2/shared/services/billing-service'
import { AnalyticsEvents, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast, infoToast, successToast } from 'v2/utils/toasts'
import { PaymentMethod } from '../billing.config'
import {
  type CashfreeCardComponents,
  type CashfreeWithComponents,
  createCardComponents,
  getCashfree,
  runCardElementsPayment,
  supportsCardComponents,
} from '../cashfree-sdk'
import { useBillingRechargeDetailsQuery } from '../queries'
import {
  buildPaymentCallbackUrl,
  getSortedRechargeChoices,
  isCashfreePaid,
  isCashfreeUserCancelled,
  verifyAndPollCashfreePayment,
} from '../recharge-shared'
import {
  BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS,
  refreshBillingAfterPayment,
} from '../refresh-billing-queries'
import { getAccountId, trackPaymentFailed } from '../utils'

interface CashfreeCardElementsAddCardProps {
  onClose: () => void
}

const GENERIC_ERROR = 'We could not add your card. Please try again.'

// Match the Stripe card form: a label + bordered box around each mounted field so
// the Cashfree iframes read as proper inputs in the console design language.
// Card entry fields (Cashfree iframe) — excluded from both session recorders
const FIELD_CLASS = `${PRIVATE_FIELD_CLASS} rounded-md border border-st2 px-3 py-2.5`

// PCI-safe Cashfree add-card using the individual card components (elements)
// instead of hosted checkout. The raw PAN is entered inside Cashfree's iframes.
// Unlike hosted checkout, this flow can tokenise the card: ticking the
// `savePaymentInstrument` component makes the SDK send save_instrument=true, and
// our verify call then syncs the saved card into the cards table. Flow:
//   1. create the card components + mount them; POST order/create -> payment_session_id
//   2. cf.pay({ paymentSessionId, paymentMethod: cardNumber, savePaymentInstrument: save })
//   3. verify + poll status; PAID => card saved (if consented) + balance topped up.
export const CashfreeCardElementsAddCard = ({ onClose }: CashfreeCardElementsAddCardProps) => {
  // The provider's client, not the rq-setup singleton: several test files
  // mock.module that singleton with a 2-3 method stub and those mocks leak
  // process-globally (AGENTS.md failure mode 1). In the app they are the same
  // object.
  const queryClient = useQueryClient()
  const accountId = getAccountId()
  const { data: rechargeDetails, isError: rechargeDetailsError } = useBillingRechargeDetailsQuery()
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // Init (create components + order) must run exactly once — a second order is a
  // second charge. Guards StrictMode's double effect and rechargeDetails refetch.
  const startedRef = useRef(false)
  const cfRef = useRef<CashfreeWithComponents | null>(null)
  const componentsRef = useRef<CashfreeCardComponents | null>(null)
  const orderRef = useRef<{ paymentSessionId: string; orderId: string } | null>(null)

  const cardHolderRef = useRef<HTMLDivElement>(null)
  const cardNumberRef = useRef<HTMLDivElement>(null)
  const cardExpiryRef = useRef<HTMLDivElement>(null)
  const cardCvvRef = useRef<HTMLDivElement>(null)
  const saveRef = useRef<HTMLDivElement>(null)

  const [error, setError] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isOrderReady, setIsOrderReady] = useState(false)
  const [isPaying, setIsPaying] = useState(false)

  useEffect(() => {
    if (startedRef.current || !rechargeDetails) return
    startedRef.current = true

    const init = async () => {
      try {
        const cf = await getCashfree()
        if (!supportsCardComponents(cf)) {
          // Merchant / cashfree-js build without web components — backend must
          // enable it; nothing the user can do from here.
          setError('Card payments are not available right now. Please contact support.')
          return
        }
        cfRef.current = cf
        const components = createCardComponents(cf)
        componentsRef.current = components
        // Mount fields first so the form renders while the order request is in flight.
        components.cardHolder.mount(cardHolderRef.current as HTMLElement)
        components.cardNumber.mount(cardNumberRef.current as HTMLElement)
        components.cardExpiry.mount(cardExpiryRef.current as HTMLElement)
        components.cardCvv.mount(cardCvvRef.current as HTMLElement)
        components.save.mount(saveRef.current as HTMLElement)
        setIsReady(true)

        // Smallest recharge choice = minimum involuntary charge (Cashfree has no
        // save-without-payment). Sorted ascending so [0] is the true minimum.
        const amount = getSortedRechargeChoices(rechargeDetails.recharge_choices)?.[0] ?? 10
        const order = await billingService.cashfreeCreateOrder({
          account_id: accountId,
          amount,
          product_type: 'plivo',
          payment_method_type: 'card',
          return_url: buildPaymentCallbackUrl('card'),
        })
        if (!order.payment_session_id || !order.order_id) {
          throw new Error('order-missing-ids')
        }
        orderRef.current = { paymentSessionId: order.payment_session_id, orderId: order.order_id }
        setIsOrderReady(true)
      } catch (err) {
        logger.error('CashfreeCardElementsAddCard: init failed', { err })
        setError('We faced an error loading the secure card form. Please try again.')
        trackPaymentFailed('add_card', err, { gateway: 'cashfree' })
      }
    }

    init()
  }, [accountId, rechargeDetails])

  const handlePay = async () => {
    const cf = cfRef.current
    const components = componentsRef.current
    const order = orderRef.current
    if (!cf || !components || !order || isPaying) return
    setIsPaying(true)
    try {
      // Pass the save component (never a hardcoded true): the SDK sends
      // save_instrument only when the user ticked it. Verify+poll confirms the
      // outcome server-side and triggers the saved-card sync into our DB.
      const result = await runCardElementsPayment({
        pay: (options) => cf.pay(options),
        verifyAndPoll: verifyAndPollCashfreePayment,
        paymentSessionId: order.paymentSessionId,
        orderId: order.orderId,
        paymentMethod: components.cardNumber,
        saveComponent: components.save,
      })
      if (result.outcome === 'error') {
        // Log the raw gateway detail; show the user a generic message — the rest
        // of the billing flow never surfaces raw PSP text (see payment-callback).
        logger.error('CashfreeCardElementsAddCard: cf.pay returned an error', {
          error: result.error,
        })
        errorToast({ title: 'Payment Failed', description: GENERIC_ERROR })
        setIsPaying(false)
        // Non-exception failure — cf.pay resolved with an error outcome, so the
        // catch below never sees it; track it or the funnel misses these.
        trackPaymentFailed('add_card', result.error, { gateway: 'cashfree' })
        return
      }
      const status = result.paymentStatus
      // The card is synced into the shared cards table by Cashfree's async
      // webhook, so poll the list until it actually appears — the PENDING branch
      // below literally promises "will appear here shortly", and before this
      // nothing ever refetched to make that true.
      //
      // BOTH targets, not just cards: this add-card is a REAL minimum-amount
      // charge, so the balance moves too. Tracking cards alone let the poll stop
      // the instant the card-sync webhook won the race, leaving a credit that
      // landed afterwards unseen — the new card beside a stale balance, which is
      // the reload-to-see-it symptom this helper exists to kill.
      //
      // Whether a card appears at all depends on consent we cannot read here: the
      // save checkbox is a Cashfree-SDK component (`components.save`), so the SDK
      // decides. Balance is the guaranteed mover; cards is best-effort. With the
      // box unticked no card ever arrives and the poll spends its full budget —
      // acceptable, because every attempt invalidates the balance anyway, so the
      // credit still lands.
      void refreshBillingAfterPayment(queryClient, accountId, {
        targets: ['balance', 'cards', 'transactions'],
        ...(status === 'PENDING'
          ? { maxAttempts: BILLING_REFRESH_PROCESSING_MAX_POLL_ATTEMPTS }
          : {}),
      })
      if (isCashfreePaid(status)) {
        track(AnalyticsEvents.CARD_ADDED, { method_type: PaymentMethod.CARD, country: 'IN' })
        successToast({ title: 'Success', description: 'Card added successfully' })
      } else if (status === 'PENDING') {
        infoToast({
          title: 'Verifying your card',
          description: 'Your card is being saved and will appear here shortly.',
        })
      } else if (!isCashfreeUserCancelled(status)) {
        track(AnalyticsEvents.PAYMENT_FAILED, {
          flow: 'add_card',
          gateway: 'cashfree',
          raw_status: status,
        })
        errorToast({ title: 'Payment Failed', description: GENERIC_ERROR })
      }
      onCloseRef.current()
    } catch (err) {
      logger.error('CashfreeCardElementsAddCard: pay flow failed', { err })
      errorToast({ title: 'Payment Failed', description: GENERIC_ERROR })
      setIsPaying(false)
      // Tracking last so a throw here can't leave the pay button stuck spinning.
      trackPaymentFailed('add_card', err, { gateway: 'cashfree' })
    }
  }

  if (error || rechargeDetailsError) {
    return (
      <div data-testid="billing-cashfree-elements-error">
        <Alert
          variant="destructive"
          title={error ?? 'Unable to load payment details. Please try again.'}
        />
      </div>
    )
  }

  // The field wrapper is always rendered and visible (never display:none): the
  // Cashfree components mount into laid-out nodes, otherwise their iframes can
  // measure a zero-size container and render broken. The loader sits above and
  // clears once the components are mounted.
  return (
    // Markers live on FIELD_CLASS per mount point, not here: the class blocks the
    // whole subtree, so marking this container would blank the labels, the
    // save-card consent and the Add Card button along with the fields.
    <div className="flex flex-col gap-3" data-testid="billing-cashfree-elements">
      {!isReady && (
        <div
          className="flex items-center justify-center py-10"
          data-testid="billing-cashfree-elements-loading"
        >
          <Loader2 className="size-6 animate-spin text-secondary" />
        </div>
      )}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-s-500 text-secondary">Name on card</span>
          <div
            ref={cardHolderRef}
            className={FIELD_CLASS}
            data-testid="billing-cashfree-field-holder"
            data-private
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-s-500 text-secondary">Card number</span>
          <div
            ref={cardNumberRef}
            className={FIELD_CLASS}
            data-testid="billing-cashfree-field-number"
            data-private
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-s-500 text-secondary">Expiry</span>
            <div
              ref={cardExpiryRef}
              className={FIELD_CLASS}
              data-testid="billing-cashfree-field-expiry"
              data-private
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-s-500 text-secondary">CVC</span>
            <div
              ref={cardCvvRef}
              className={FIELD_CLASS}
              data-testid="billing-cashfree-field-cvv"
              data-private
            />
          </div>
        </div>
        <div ref={saveRef} data-testid="billing-cashfree-field-save" />
        <ButtonWithLoader
          type="button"
          onClick={handlePay}
          loading={isPaying}
          disabled={!isOrderReady}
          data-testid="billing-cashfree-elements-pay-btn"
        >
          Add Card
        </ButtonWithLoader>
      </div>
    </div>
  )
}
